import {
  Button,
  DraggableWindowHeader,
  Dropdown,
  Icon,
  type Option,
} from "@hoylu/client-common";
import { clsx } from "clsx";
import React, {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import type { AppState } from "../../state/root.reducer";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import {
  selectDashboardWorkspaceDetailsById,
  selectSelectedWorkspace,
  uploadedSnapshot,
} from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import { useSingleSelectDropdownTranslation } from "../../utils/hooks/useSingleSelectDropdownTranslation";
import SimpleWorkspaceCard from "../cards/SimpleWorkspaceCard";
import { Modal } from "./Modal";
import Styles from "./ThumbnailEditorDialog.module.css";

enum ThumbnailType {
  EMOJI,
  TEMPLATE,
  CUSTOM,
  SNAPSHOT,
}

type ThumbnailSelectorProps = {
  setBlob: Dispatch<SetStateAction<Blob | null>>;
};

const acceptedImageTypes = ["image/gif", "image/jpeg", "image/png"];
const allowedFileSizeMB = 1; // in MB

const CustomThumbnailSelector = ({ setBlob }: ThumbnailSelectorProps) => {
  const t = useI18n("THUMBNAIL_EDITOR.");

  const inputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState(() => t("DRAG_DROP"));
  const [err, setErr] = useState<string | null>(null);

  const checkAcceptedTypeError = (file?: File): string | null => {
    if (!file || acceptedImageTypes.includes(file.type)) return null;
    return t("ERR");
  };

  const checkSizeError = (file?: File): string | null => {
    if (!file || allowedFileSizeMB * 1024 * 1024 >= file.size) return null;
    return t("ERR");
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const image = e.target.files?.[0];
    const err = checkAcceptedTypeError(image) ?? checkSizeError(image);
    if (image && !err) {
      setErr(null);
      setFileName(image.name);
      setBlob(image);
    } else {
      setErr(err);
      setBlob(null);
    }
  };

  const onFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const image = e.dataTransfer.files[0];
    const err = checkAcceptedTypeError(image) ?? checkSizeError(image);
    if (image && !err) {
      setErr(null);
      setFileName(image.name);
      setBlob(image);
    } else {
      setErr(err);
      setBlob(null);
    }
  };

  const allowDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className={clsx(
        Styles.customThumbnailSelector,
        err ? Styles.borderAttention : Styles.borderIndicator,
      )}
      onDragOver={(e) => allowDrop(e)}
      onDrop={(e) => onFileDrop(e)}
    >
      <input
        accept={acceptedImageTypes.join(", ")}
        className={Styles.hidden}
        data-test-id="hidden-input"
        onChange={onFileChange}
        ref={inputRef}
        type="file"
      />
      {err && (
        <span className={Styles.error} title={err}>
          {err}
        </span>
      )}
      {!err && (
        <span className={Styles.fileName} title={fileName}>
          {fileName}
        </span>
      )}
      <Button
        onClick={() => inputRef.current?.click()}
        toolTip={t("BROWSE")}
        type="primary"
      >
        {t("BROWSE")}
      </Button>
    </div>
  );
};

const SnapshotSelector = () => {
  const dispatch = useDispatch();
  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace)!;

  const t = useI18n("THUMBNAIL_EDITOR.");

  if (!selectedMyWorkspaceMetadata) return null;

  return (
    <>
      {/* FIXME: There doesn't appear to be any camera icon fonts exported from client-common. Should this Icon component be removed, or does a "camera" icon need to be defined in client-common? */}
      <Icon icon="hoylu-ui-icons-camera" />
      <Button
        onClick={() =>
          dispatch(
            workspacesActions.workspaceSnapshot.request({
              workspaceId: selectedMyWorkspaceMetadata.workspaceId,
            }),
          )
        }
        toolTip={t("TAKE_SNAPSHOT")}
        type="primary"
      >
        {t("TAKE_SNAPSHOT")}
      </Button>
    </>
  );
};

export const ThumbnailEditorDialog = () => {
  const dispatch = useDispatch();

  const t = useI18n("THUMBNAIL_EDITOR.");
  const tClose = useI18n("CLOSE_BUTTON.");
  const dropdownTranslation = useSingleSelectDropdownTranslation();

  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace)!;
  const selectedDashboardWorkspaceDetail = useSelector((state: AppState) =>
    selectDashboardWorkspaceDetailsById(
      state,
      selectedMyWorkspaceMetadata.workspaceId,
    ),
  );
  const snapshot = useSelector((state: RootState) =>
    uploadedSnapshot(state, selectedMyWorkspaceMetadata.workspaceId),
  );

  const close = useCallback(
    (closeAll: boolean = false) =>
      dispatch(
        closeAll
          ? workspacesActions.cancelDashboardOption()
          : workspacesActions.editThumbnail({ flag: false }),
      ),
    [],
  );

  const typeOptions = useMemo<Option<ThumbnailType>[]>(() => {
    const options = [
      //TODO: Part of future work
      // {label: "Emoji", value: ThumbnailType.EMOJI},
      // {label: "Template", value: ThumbnailType.TEMPLATE},
      { label: t("CUSTOM_IMAGE"), value: ThumbnailType.CUSTOM },
    ];
    if (selectedDashboardWorkspaceDetail?.isLive) {
      options.push({ label: t("SNAPSHOT"), value: ThumbnailType.SNAPSHOT });
    }
    return options;
  }, [selectedDashboardWorkspaceDetail?.isLive, t]);

  const [thumbnailType, setThumbnailType] = useState<Option<ThumbnailType>[]>([
    typeOptions[0],
  ]);
  const [thumbnailUrl, setThumbnailUrl] = useState(
    selectedDashboardWorkspaceDetail?.thumbnailUrl,
  );
  const [newThumbnailBlob, setNewThumbnailBlob] = useState<Blob | null>(null);

  const blob = useMemo(
    () => snapshot ?? newThumbnailBlob,
    [snapshot, newThumbnailBlob],
  );

  useEffect(() => {
    let tmpUri: string;
    if (blob) {
      tmpUri = URL.createObjectURL(blob);
      setThumbnailUrl(tmpUri);
    }
    return () => {
      tmpUri && URL.revokeObjectURL(tmpUri);
    };
  }, [blob]);

  if (!selectedMyWorkspaceMetadata) return null;

  return (
    <Modal allowContentToOverflow={true} handleOutsideClick={() => close()}>
      <DraggableWindowHeader
        close={tClose("CLOSE")}
        onClose={() => close(false)}
        title={`${t("TITLE")}: ${selectedMyWorkspaceMetadata?.workspaceId}`}
      />
      <div className={Styles.workspaceCardEditor}>
        <SimpleWorkspaceCard
          myWorkspaceMetadata={selectedMyWorkspaceMetadata}
          thumbnailPreviewUrl={thumbnailUrl}
        >
          <span
            className={Styles.truncate}
            title={selectedMyWorkspaceMetadata.workspaceName ?? ""}
          >
            {selectedMyWorkspaceMetadata.workspaceName}
          </span>
        </SimpleWorkspaceCard>
        <div className={Styles.selectors}>
          <Dropdown
            allowReset={false}
            localizationDict={dropdownTranslation}
            onChange={setThumbnailType}
            options={typeOptions}
            showLabel
            title={t("TYPE")}
            values={thumbnailType}
          />
          {thumbnailType[0].value === ThumbnailType.CUSTOM && (
            <CustomThumbnailSelector setBlob={setNewThumbnailBlob} />
          )}
          {thumbnailType[0].value === ThumbnailType.SNAPSHOT && (
            <SnapshotSelector />
          )}
        </div>
      </div>
      <div className={Styles.footer}>
        <Button
          classNames={[Styles.marginAutoRight]}
          onClick={() => {
            dispatch(
              workspacesActions.removeThumbnail.request({
                workspaceId: selectedMyWorkspaceMetadata.workspaceId,
              }),
            );
            close(false);
          }}
          toolTip={t("DELETE")}
          type="secondary"
        >
          {t("DELETE")}
        </Button>
        <Button onClick={() => close()} toolTip={t("CANCEL")} type="secondary">
          {t("CANCEL")}
        </Button>
        <Button
          disabled={!blob}
          onClick={() => {
            blob &&
              dispatch(
                workspacesActions.uploadThumbnail.request({
                  workspaceId: selectedMyWorkspaceMetadata.workspaceId,
                  file: blob,
                }),
              );
            close(false);
          }}
          toolTip={t("SAVE")}
          type="primary"
        >
          {t("SAVE")}
        </Button>
      </div>
    </Modal>
  );
};
